import React, { useEffect, useState } from "react";
import { QuillLogo } from "../components/QuillLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INK = "#1C1A17";
const PAPER = "#F4F1EA";
const PAPER_DEEP = "#ECE7DC";
const OXBLOOD = "#7A2E2E";
const OXBLOOD_WARM = "#B0573F";
const STONE = "#A8A296";
const SLATE = "#6E6A5E";

const body = { fontFamily: "Lato, system-ui, sans-serif" };
const fieldStyle = { ...body, borderBottom: `1px solid ${STONE}`, color: INK, backgroundColor: "transparent" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[12px] uppercase" style={{ ...body, letterSpacing: "0.12em", color: SLATE }}>{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const BookEbookCheckout = () => {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "" });
  const [winnow, setWinnow] = useState(""); // honeypot, kept out of autofill
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Buy the ebook · No One Is Coming To Save You";
    fetch(`${API}/book/config`).then((r) => r.json()).then(setConfig).catch(() => setConfig(null));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const price = config && config.ebook_price ? config.ebook_price : "147.00";

  const pay = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg("");
    try {
      const postCheckout = () => fetch(`${API}/book/ebook/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, company: winnow }),
      });
      let res;
      try {
        res = await postCheckout();
      } catch {
        await new Promise((r) => setTimeout(r, 1200));
        res = await postCheckout();
      }
      if (res.status === 503) {
        setMsg("The ebook shop is being set up. Please use a retailer, or write to us at info@theravensquill.co.za.");
        return;
      }
      if (res.status === 400) {
        setMsg("We couldn't verify the form. Please refresh the page and try again, or email info@theravensquill.co.za.");
        return;
      }
      if (!res.ok) throw new Error("checkout failed");
      const { process_url, fields } = await res.json();
      const f = document.createElement("form");
      f.method = "POST";
      f.action = process_url;
      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        f.appendChild(input);
      });
      document.body.appendChild(f);
      f.submit();
    } catch {
      setMsg("We could not open checkout just now. Please try again, or write to us.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ backgroundColor: PAPER, color: INK, minHeight: "100vh" }} data-testid="ebook-checkout-page">
      <div style={{ backgroundColor: "#120C08" }} className="py-8">
        <div className="mx-auto max-w-[920px] px-6 flex items-center justify-between">
          <a href="/no-one-is-coming" className="flex items-center gap-3" data-testid="ebook-checkout-back-link">
            <QuillLogo height={38} variant="gold" />
            <span className="font-serif italic text-[18px]" style={{ color: PAPER }}>The Raven&rsquo;s Quill</span>
          </a>
          <span className="text-[11px] uppercase font-serif" style={{ letterSpacing: "0.2em", color: OXBLOOD_WARM }}>
            Ebook
          </span>
        </div>
      </div>

      <form onSubmit={pay} className="mx-auto max-w-[920px] px-6 py-14 grid md:grid-cols-[1fr_340px] gap-12" data-testid="ebook-checkout-form">
        <div>
          <span className="text-[11px] uppercase font-serif" style={{ letterSpacing: "0.25em", color: OXBLOOD }}>Your details</span>
          <span className="mt-4 block" style={{ width: 64, height: "1px", backgroundColor: OXBLOOD }} />

          {msg && (
            <p className="mt-6 text-[14px] font-serif italic" style={{ color: OXBLOOD }} data-testid="ebook-checkout-message">{msg}</p>
          )}

          <div className="mt-8 grid gap-6">
            <Field label="Full name">
              <input required value={form.full_name} onChange={set("full_name")} data-testid="ebook-checkout-name" className="w-full py-2.5 outline-none rounded-none text-[16px]" style={fieldStyle} />
            </Field>
            <Field label="Email (your ebook is delivered here)">
              <input required type="email" value={form.email} onChange={set("email")} data-testid="ebook-checkout-email" className="w-full py-2.5 outline-none rounded-none text-[16px]" style={fieldStyle} />
            </Field>

            {/* honeypot: hidden from humans and autofill */}
            <div style={{ display: "none" }} aria-hidden="true">
              <input type="text" tabIndex={-1} autoComplete="new-password" name="winnow_ref_xq" value={winnow} onChange={(e) => setWinnow(e.target.value)} />
            </div>
          </div>

          <p className="mt-8 text-[13px] leading-[1.7]" style={{ ...body, color: SLATE, maxWidth: "56ch" }}>
            The ebook is delivered instantly after payment, as a secure, personal download link shown on the next page and emailed to you. Each copy is lightly marked with your name.
          </p>
        </div>

        {/* RIGHT: summary */}
        <div>
          <div style={{ backgroundColor: PAPER_DEEP }} className="p-7">
            <span className="text-[11px] uppercase font-serif" style={{ letterSpacing: "0.22em", color: OXBLOOD }}>Order summary</span>
            <span className="mt-4 block" style={{ width: 48, height: "1px", backgroundColor: OXBLOOD }} />
            <div className="mt-6 flex items-baseline justify-between text-[15px]" style={body}>
              <span>Ebook (instant download)</span>
              <span>R{price}</span>
            </div>
            <div className="mt-6 pt-4 flex items-baseline justify-between text-[17px] font-serif" style={{ borderTop: `1px solid ${STONE}` }}>
              <span>Total</span>
              <span data-testid="ebook-total">R{price}</span>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="ebook-pay-button"
              className="mt-7 w-full px-6 py-4 text-[13px] uppercase font-serif transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{ letterSpacing: "0.18em", backgroundColor: OXBLOOD, color: PAPER }}
            >
              {busy ? "Opening PayFast" : "Pay now with PayFast"}
            </button>
            <p className="mt-4 text-[12px]" style={{ ...body, color: SLATE }}>
              Secure payment by PayFast. Delivered instantly on payment.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BookEbookCheckout;
